import React, {ReactNode, useCallback, useLayoutEffect, useRef} from "react";
import {IEvent} from "@webng-types/write-model";
import {useTranslation} from "react-i18next";
import {ChevronLeft, ChevronRight, ChevronsLeft} from "react-feather";
import {Star} from "react-feather";
import {Pin, Summary, CircleLoader} from "@webng/icons-common"
import {useNiceDateTime} from "@webng/react-app-common";
import {useHorizontalTimeline} from "./useHorizontalTimeline";
import {useDateTimeDisplay} from "./LiveblogRenderContext";
import { isSummaryEvent } from "./utils/systemTags";
import {isHighlightedEvent, isStickyEvent as isStickyEventFn} from "./utils/eventHighlight";
import { EventSportIcon } from "./event/EventSportIcon";
import {getSportTiming} from "./utils/eventUtils";

export interface HorizontalTimelineItem {
  event: IEvent
  node: ReactNode
}

interface HorizontalTimelineContainerProps {
  items: HorizontalTimelineItem[]
  // true when refresh is `auto`: a live update prepends in the background and must hold the
  // reader's position; when false (`notify`/`off`) a live prepend is a solicited apply → newest.
  autoRefresh?: boolean
  loadMoreOlderLink?: string
  onLoadMoreOlder?: (e: React.UIEvent) => void
  isLoadingOlder?: boolean
  // newer-side controls live at the strip's left edge (toward live), mirroring the older
  // load-more at the right edge; only present in a deep-linked / scrolled-back window.
  loadMoreNewerLink?: string
  onLoadMoreNewer?: (e: React.UIEvent) => void
  isLoadingNewer?: boolean
  backToLiveLink?: string
  onBackToLive?: (e: React.UIEvent) => void
  isLoadingBackToLive?: boolean
}

const HorizontalTimelineAxisDots = React.memo(function HorizontalTimelineAxisDots({onClick}: {onClick: (e: React.UIEvent) => void}) {
  return <button type="button" className="tik4-htl__axis-dots" onClick={onClick} tabIndex={-1} aria-hidden="true">
    <span className="tik4-htl__tick__dot"/>
    <span className="tik4-htl__tick__dot"/>
    <span className="tik4-htl__tick__dot"/>
  </button>
})

const HorizontalTimelineTick = React.memo(function HorizontalTimelineTick({event, index, active, onSelect}: {
  event: IEvent
  index: number
  active: boolean
  onSelect: (index: number) => void
}) {
  const {t} = useTranslation("liveblog")
  const dateTimeDisplay = useDateTimeDisplay()
  const {time, date, isoString} = useNiceDateTime(event.created_at || "now", undefined, event.display_time, dateTimeDisplay)
  // a present display_time override is the only thing shown, beating sport timing
  const sportTiming = event.display_time != null ? undefined : getSportTiming(event)
  const tickTime = sportTiming ? (sportTiming.timeOfPlay || sportTiming.phase?.name) : time
  const tickDate = sportTiming ? (sportTiming.timeOfPlay ? sportTiming.phase?.name : undefined) : date
  
  const isHighlightEvent = isHighlightedEvent(event)
  const isStickyEvent = isStickyEventFn(event)
  const isSummary = isSummaryEvent(event)
  const sportEvent = event.sport_event

  function PrimaryTickLabel({time, date, className}: {time?: string, date?: string, className?: string}) {
    const hasSportIcon = sportEvent && sportEvent.event_type && sportEvent.event_type !== "generic"

    return <span className={`tik4-htl__tick__primary ${className || ""}`}>
      
      {(isStickyEvent || isHighlightEvent || hasSportIcon || isSummary) && (
          <span className="tik4-htl-icons">
            { isHighlightEvent &&
              <Star className="tik4-icon tik4-htl-icons__icon tik4-htl-icons__icon--highlight"
                  aria-label={t('liveblog.event.highlight.label')}/>
            }
            { isStickyEvent &&
              <Pin className="tik4-icon tik4-htl-icons__icon tik4-htl-icons__icon--pin"
                    aria-label={t('liveblog.event.sticky.label')}/>
            }
            { isSummary &&
              <Summary className="tik4-icon tik4-htl-icons__icon tik4-htl-icons__icon--summary"
                    aria-label={t('liveblog.event.summary.title')}/>
            }
            { hasSportIcon && <EventSportIcon className="tik4-icon tik4-icon--sport tik4-htl-icons__icon tik4-htl-icons__icon--sport" block={sportEvent} />}
          </span>
      )}

      {(time || date) &&
        <time className="tik4-htl__tick__time-or-date" dateTime={isoString}>
          {time || date}
        </time>
      }
      {isSummary && <span className="tik4-htl__tick__primary__title ">{t("liveblog.event.summary.title")}</span>}
    </span>
  }

  function SecondaryTickLabel({time, date, className}: {time?: string, date?: string, className?: string}) {
    if (time && date) {
      return <span className={`tik4-htl__tick__secondary ${className || ""}`}>{date}</span>
    } else {
      return null
    }
  }

  return <button type="button" data-tick className={`tik4-htl__tick${active ? " tik4-htl__tick--active" : ""} ${isSummary ? "tik4-htl__tick--summary" : ""} ${isHighlightEvent ? "tik4-htl__tick--highlight" : ""} ${isStickyEvent ? "tik4-htl__tick--pin" : ""}`}
                 onClick={() => onSelect(index)}>

    <SecondaryTickLabel time={tickTime} date={tickDate}/>
    <SecondaryTickLabel time={tickTime} date={tickDate} className="tik4-htl__tick__secondary--bold-placeholder"/>
                
    
    <PrimaryTickLabel time={tickTime} date={tickDate}/>
    <PrimaryTickLabel time={tickTime} date={tickDate} className="tik4-htl__tick__primary--bold-placeholder"/>
  
    <span className="tik4-htl__tick__dot"/>
  </button>
})

export function HorizontalTimelineContainer({items, autoRefresh, loadMoreOlderLink, onLoadMoreOlder, isLoadingOlder,
                                              loadMoreNewerLink, onLoadMoreNewer, isLoadingNewer,
                                              backToLiveLink, onBackToLive, isLoadingBackToLive}: HorizontalTimelineContainerProps) {
  const {t} = useTranslation("liveblog")
  const carouselRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const axisRef = useRef<HTMLDivElement>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  // windowed = deep-linked / scrolled back into history, where the left-edge controls appear and
  // background refresh is suspended. Routes how a newer prepend moves focus.
  const isWindowed = !!(backToLiveLink || loadMoreNewerLink)
  // set on click so the hook routes the ensuing refresh to the newest post (index 0) instead of
  // treating an overlapping live set as a "load more newer" prepend.
  const backToLivePendingRef = useRef(false)
  const {active, atEnd, goTo, goToStart, goToEnd, step} = useHorizontalTimeline(carouselRef, trackRef, axisRef, items, isWindowed, !!autoRefresh, backToLivePendingRef)
  // stable so memoized ticks only re-render when their own active flag flips
  const selectTick = useCallback((index: number) => goTo(index, true), [goTo])

  const handleBackToLive = useCallback((e: React.UIEvent) => {
    backToLivePendingRef.current = true
    onBackToLive?.(e)
  }, [onBackToLive])

  const handleNavRight = useCallback((e: React.UIEvent) => {
    if (atEnd && onLoadMoreOlder) onLoadMoreOlder(e)
    else step(1)
  }, [atEnd, onLoadMoreOlder, step])

  const handleNavLeft = useCallback((e: React.UIEvent) => {
    if (active === 0 && onLoadMoreNewer) onLoadMoreNewer(e)
    else step(-1)
  }, [active, onLoadMoreNewer, step])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") { e.preventDefault(); handleNavRight(e) }
    else if (e.key === "ArrowLeft") { e.preventDefault(); handleNavLeft(e) }
  }, [handleNavRight, handleNavLeft])

  // Measure the rendered width of the load-more button and expose it as --t4-htl-more-w on the
  // strip so the CSS last-card narrowing (tik4-horizontal-timeline.css) is accurate across all
  // locales. useLayoutEffect gives the initial value before the first paint; ResizeObserver
  // keeps it in sync on font/viewport changes.
  useLayoutEffect(() => {
    const more = moreRef.current
    const strip = carouselRef.current
    if (!strip) return
    if (!more) {
      strip.style.removeProperty('--t4-htl-more-w')
      return
    }
    const update = () => strip.style.setProperty('--t4-htl-more-w', `${more.offsetWidth}px`)
    update()
    if (typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(update)
    ro.observe(more)
    return () => ro.disconnect()
  }, [loadMoreOlderLink])

  return <div className={`tik4-htl${atEnd ? " tik4-htl--at-end" : ""}`}>
    <div className="tik4-htl__live" role="status" aria-live="polite">
      {items.length ? t('liveblog.timeline.position', {current: active + 1, total: items.length}) : ''}
    </div>

    <div ref={axisRef} className="tik4-htl__axis">
      <div ref={trackRef} className="tik4-htl__axis__track">
        {(backToLiveLink || loadMoreNewerLink) &&
          <HorizontalTimelineAxisDots onClick={goToStart}/>
        }
        {items.map((item, i) =>
          <HorizontalTimelineTick key={item.event.local_id} event={item.event} index={i}
                                  active={i === active} onSelect={selectTick}/>
        )}
        {loadMoreOlderLink &&
          <HorizontalTimelineAxisDots onClick={goToEnd}/>
        }
      </div>
    </div>

    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions -- focusable, keyboard-operable horizontal scroll region */}
    <div ref={carouselRef} className="tik4-htl__strip tik4-event-list" tabIndex={0} role="region" aria-label={t('liveblog.timeline.label')} onKeyDown={handleKeyDown}>
      {(backToLiveLink || loadMoreNewerLink) &&
        <div className="tik4-htl__lead">
          {backToLiveLink &&
            <a className="tik4-htl__lead__button tik4-button tik4-button--outline-dark"
               href={backToLiveLink} onClick={handleBackToLive}>
              {isLoadingBackToLive
                ? <CircleLoader className="tik4-icon tik4-spin"/>
                : <ChevronsLeft className="tik4-icon"/>}
              <span className="tik4-htl__lead__button__label">{t('liveblog.back_to_live.button')}</span>
            </a>
          }
          {loadMoreNewerLink &&
            <a className="tik4-htl__lead__button tik4-button tik4-button--outline-dark"
               href={loadMoreNewerLink} onClick={onLoadMoreNewer}>
              {isLoadingNewer
                ? <CircleLoader className="tik4-icon tik4-spin"/>
                : <ChevronLeft className="tik4-icon"/>}
              <span className="tik4-htl__lead__button__label">{t('liveblog.load_more_top.button')}</span>
            </a>
          }
        </div>
      }
      {items.map((item, i) =>
        <div key={item.event.local_id} data-card data-id={item.event.local_id ?? ""}
             className={`tik4-htl__card${i === active ? " tik4-htl__card--active" : ""}`}
             aria-current={i === active ? "true" : undefined}
             tabIndex={i === active ? 0 : -1} aria-hidden={i === active ? "false" : "true"}>
          {item.node}
        </div>
      )}
      {loadMoreOlderLink &&
        <div ref={moreRef} className="tik4-htl__more">
          <a className="tik4-htl__more__button tik4-button tik4-button--outline-dark"
             href={loadMoreOlderLink} onClick={onLoadMoreOlder}>
            {isLoadingOlder
              ? <CircleLoader className="tik4-icon tik4-icon--mr tik4-spin"/>
              : <ChevronRight className="tik4-icon tik4-icon--mr"/>}
            {t('liveblog.load_more_bottom.button')}
          </a>
        </div>
      }
    </div>

    <div className="tik4-htl__nav">
      <button type="button" className="tik4-htl__nav__button tik4-link tik-link--muted" onClick={handleNavLeft}
              aria-label={t('liveblog.action.previous')}
              disabled={active <= 0 && !loadMoreNewerLink}>
        <ChevronLeft className="tik4-icon tik4-htl__nav__icon"/>
      </button>
      <button type="button" className="tik4-htl__nav__button tik4-link tik-link--muted" onClick={handleNavRight}
              aria-label={t('liveblog.action.next')}>
        <ChevronRight className="tik4-icon tik4-htl__nav__icon"/>
      </button>
    </div>
  </div>
}
